import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * XHR-based session exchange to avoid "postMessage: Request object could not be cloned"
 * errors caused by CDN/proxy service workers intercepting fetch() requests.
 */
const xhrSessionExchange = (sessionId) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/auth/session`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          reject(new Error(data.detail || 'Authentication failed'));
        }
      } catch {
        reject(new Error('Authentication failed'));
      }
    };
    xhr.onerror = () => {
      // Retry with same-origin if cross-origin XHR failed
      const retryXhr = new XMLHttpRequest();
      retryXhr.open('POST', `${window.location.origin}/api/auth/session`);
      retryXhr.setRequestHeader('Content-Type', 'application/json');
      retryXhr.onload = () => {
        try {
          const data = JSON.parse(retryXhr.responseText);
          if (retryXhr.status >= 200 && retryXhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || 'Authentication failed'));
          }
        } catch {
          reject(new Error('Authentication failed'));
        }
      };
      retryXhr.onerror = () => reject(new Error('Unable to connect to server'));
      retryXhr.send(JSON.stringify({ session_id: sessionId }));
    };
    xhr.send(JSON.stringify({ session_id: sessionId }));
  });
};

const BTTAuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        console.error('No session_id found in URL hash');
        navigate('/login');
        return;
      }

      try {
        const data = await xhrSessionExchange(sessionId);

        // Store token for BTT auth
        if (data.token) {
          localStorage.setItem('btt_token', data.token);
        }

        // Clear URL fragment
        window.history.replaceState(null, '', window.location.pathname);

        // Navigate to platform app
        navigate('/platform/app');
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#00A86B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#00A86B] text-sm uppercase tracking-widest">Authenticating...</p>
      </div>
    </div>
  );
};

export default BTTAuthCallback;
